import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";

// components/winners/WinnerStatsCards.jsx
export function WinnerStatsCards({ winners = [] }) {
  const { language } = useLanguage();
  const t =
    giveAwayContent[language]?.winnerStats || giveAwayContent.de?.winnerStats;

  // ✅ Robustere Status-Erkennung mit Fallback
  const getStatsForStatus = (status) => {
    if (!winners || winners.length === 0) return 0;

    return winners.filter((winner) => {
      const winnerStatus =
        winner.notificationStatus || winner.status || "pending";
      return winnerStatus === status;
    }).length;
  };

  const totalWinners = winners ? winners.length : 0;

  // ✅ Berechne zusätzliche Statistiken
  const getActionRequiredCount = () => {
    if (!winners || winners.length === 0) return 0;

    return winners.filter((winner) => {
      const status = winner.notificationStatus || winner.status || "pending";
      const hasVoucher =
        winner.prize?.voucherCode && winner.prize.voucherCode.trim() !== "";

      // Pending oder contacted ohne Gutschein = Action Required
      return status === "pending" || (status === "contacted" && !hasVoucher);
    }).length;
  };

  const actionRequiredCount = getActionRequiredCount();

  const stats = [
    {
      title: t?.stats?.total || "Gesamt",
      value: totalWinners,
      bgColor: "bg-gray-100",
      textColor: "text-gray-800",
      icon: t?.icons?.total || "🏆",
    },
    {
      title: t?.stats?.pending || "Ausstehend",
      value: getStatsForStatus("pending"),
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
      icon: t?.icons?.pending || "⏳",
    },
    {
      title: t?.stats?.contacted || "Kontaktiert",
      value: getStatsForStatus("contacted"),
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
      icon: t?.icons?.contacted || "📞",
    },
    {
      title: t?.stats?.voucherSent || "Gutschein gesendet",
      value: getStatsForStatus("voucher_sent"),
      bgColor: "bg-orange-100",
      textColor: "text-orange-800",
      icon: t?.icons?.voucherSent || "📧",
    },
    {
      title: t?.stats?.completed || "Abgeschlossen",
      value: getStatsForStatus("completed"),
      bgColor: "bg-green-100",
      textColor: "text-green-800",
      icon: t?.icons?.completed || "✅",
    },
  ];

  // ✅ Action Required Card hinzufügen wenn Aktionen nötig sind
  if (actionRequiredCount > 0) {
    stats.splice(1, 0, {
      title: t?.stats?.actionRequired || "Aktion erforderlich",
      value: actionRequiredCount,
      bgColor: "bg-red-100",
      textColor: "text-red-800",
      icon: t?.icons?.actionRequired || "🚨",
      isUrgent: true,
    });
  }

  // Leerer State
  if (totalWinners === 0) {
    return (
      <div className="mb-8 bg-white p-6 rounded-lg shadow text-center">
        <div className="text-gray-400 text-4xl mb-3">🏆</div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {t?.emptyState?.title || "Keine Gewinner vorhanden"}
        </h3>
        <p className="text-gray-500">
          {t?.emptyState?.description ||
            "Es wurden noch keine Gewinner gezogen."}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {stats.map((stat, index) => (
        <div
          key={index}
          className={`bg-white p-4 sm:p-6 rounded-lg shadow hover:shadow-lg transition-shadow ${
            stat.isUrgent ? "ring-2 ring-red-200" : ""
          }`}
        >
          <div className="flex items-center">
            <div
              className={`p-2 sm:p-3 rounded-lg ${
                stat.bgColor
              } mr-3 sm:mr-4 flex items-center justify-center flex-shrink-0 ${
                stat.isUrgent ? "animate-pulse" : ""
              }`}
            >
              <div className="text-xl sm:text-2xl">{stat.icon}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-xl sm:text-2xl ${stat.textColor} font-bold mb-1`}
              >
                {stat.value}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {stat.title}
              </p>
              {stat.title !== (t?.stats?.total || "Gesamt") &&
                totalWinners > 0 && (
                  <p className="text-xs text-gray-400">
                    {Math.round((stat.value / totalWinners) * 100)}%
                  </p>
                )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
